import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Form, Input, Select, Checkbox, Button, Tabs, Card, Space, DatePicker, TimePicker } from 'antd';
import moment from 'moment';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { DeviceType, RulesWithDeviceType, SuperGroup, DeviceTypeSettings, Version, ApiError } from '@services/types';

import styles from './rules.module.scss';
import api from '@services/api';
import { toast } from 'react-toastify';
import { selectSearchFilter } from '@services/utils';
import { AppContext } from '@layouts/AppRouter';
import { UpgradeRuleValidateInfoResponse } from '@openapi';
import { useHistory, useParams } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import WarningModal from './components/WarningModal';
import VersionSelect from './components/VersionSelect';
import ChainingRules from './components/ChainingRules';
import VersionComparisonSection from './components/VersionComparisonSection';
import ExtendedVersioningCheckbox from './components/ExtendedVersioningCheckbox';
import { first, forEach, last, map } from 'lodash';

const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT/AEST)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZDT/NZST)' },
] as const;

const handleChainingRuleRequest = (prev: any[], next: any[]) => {
  const nextById = new Map(next.map((n, i) => [n.id, { ...n, orderIndex: i }]));

  const result = [...map(next, (nxt, i) => ({ ...nxt, orderIndex: i }))];

  forEach(prev, (prv) => {
    if (!nextById.has(prv.id)) {
      result.push({ id: prv.id, isDeleted: true } as any);
    }
  });
  const buildOptionMapper = () => {
    if (next.length === 0) return {};

    const mainRule = next[next.length - 1];
    const optionMapper: any = {
      loaderVersionFirmwareId: mainRule?.loaderVersion,
      fwVersionFirmwareId: mainRule?.fwVersion,
      uiVersionFirmwareId: mainRule?.uiVersion,
    };

    return optionMapper;
  };

  return { ...buildOptionMapper(), chainingRules: result };
};

const { Option } = Select;

const useExtendedVersioning = (initialRule?: RulesWithDeviceType) => {
  const [extendedVersioning, setExtendedVersioning] = useState({
    loader: initialRule?.disableExtendedVersioningSettings?.disableExtendedLoaderVersioning || false,
    fw: initialRule?.disableExtendedVersioningSettings?.disableExtendedFwVersioning || false,
    ui: initialRule?.disableExtendedVersioningSettings?.disableExtendedUiVersioning || false,
    customFw1: initialRule?.disableExtendedVersioningSettings?.disableExtendedCustomFw1Versioning || false,
    customFw2: initialRule?.disableExtendedVersioningSettings?.disableExtendedCustomFw2Versioning || false,
    customFw3: initialRule?.disableExtendedVersioningSettings?.disableExtendedCustomFw3Versioning || false,
  });

  const updateExtendedVersioning = useCallback((key: keyof typeof extendedVersioning) => {
    setExtendedVersioning((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return { extendedVersioning, updateExtendedVersioning, setExtendedVersioning };
};

const useVersionComparison = (initialRule?: RulesWithDeviceType) => {
  const [versionComparison, setVersionComparison] = useState({
    fw: false,
    ui: false,
    loader: false,
  });

  const updateVersionComparison = useCallback((key: keyof typeof versionComparison) => {
    setVersionComparison((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useEffect(() => {
    setVersionComparison({
      fw: initialRule?.enableFwCondition ?? false,
      ui: initialRule?.enableUiCondition ?? false,
      loader: initialRule?.enableLoaderCondition ?? false,
    });
  }, [initialRule?.enableFwCondition, initialRule?.enableUiCondition, initialRule?.enableLoaderCondition]);

  return { versionComparison, updateVersionComparison, setVersionComparison };
};

const EditRuleModal = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { settings } = useContext(AppContext);
  const [form] = Form.useForm<RulesWithDeviceType>();
  const [ruleToUpdate, setRuleToUpdate] = useState<RulesWithDeviceType>();
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [superGroups, setSuperGroups] = useState<SuperGroup[]>([]);
  const [deviceSettings, setDeviceSettings] = useState<DeviceTypeSettings>();
  const [deviceVersion, setDeviceVersion] = useState<Version>();
  const [deviceTypeId, setDeviceTypeId] = useState<DeviceType['id']>('');
  const [enableChainingRule, setEnableChainingRule] = useState<boolean>(false);
  const [visibleWarningModal, setVisibleWarningModal] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('1');
  const [accessTab, setAccessTab] = useState<string>('1');
  const [warningData, setWarningData] = useState<UpgradeRuleValidateInfoResponse>();
  const [enableDateRange, setEnableDateRange] = useState<boolean>(false);
  const [enableTimeRange, setEnableTimeRange] = useState<boolean>(false);

  const { extendedVersioning, updateExtendedVersioning } = useExtendedVersioning(ruleToUpdate);
  const { versionComparison, updateVersionComparison } = useVersionComparison(ruleToUpdate);

  const isEditMode = Boolean(id);
  const currentDeviceTypeId = deviceTypeId || ruleToUpdate?.deviceTypeId;

  const initialFormValues = useMemo(
    () => ({
      deviceTypeId: null,
      name: null,
      superGroupId: null,
      forceUpdate: null,
      loaderVersionFirmwareId: null,
      fwVersionFirmwareId: null,
      uiVersionFirmwareId: null,
      sendAllFirmwares: null,
      customFirmware1VersionId: null,
      customFirmware2VersionId: null,
      customFirmware3VersionId: null,
      enableLoaderCondition: false,
      enableUiCondition: false,
      enableFwCondition: false,
      enableChainingRule: false,
      disableExtendedVersioningSettings: {
        disableExtendedLoaderVersioning: false,
        disableExtendedFwVersioning: false,
        disableExtendedUiVersioning: false,
        disableExtendedCustomFw1Versioning: false,
        disableExtendedCustomFw2Versioning: false,
        disableExtendedCustomFw3Versioning: false,
      },
      chainingRules: [],
      updateWindowStartDate: null,
      updateWindowEndDate: null,
      updateWindowStartTime: null,
      updateWindowEndTime: null,
      updateWindowTimezone: 'UTC',
    }),
    [],
  );

  const handleNext = useCallback(() => {
    if (activeTab === '1') {
      setActiveTab('2');
      setAccessTab('2');
    } else if (activeTab === '2') {
      setActiveTab('3');
      setAccessTab('3');
    }
  }, [activeTab]);

  const onClose = useCallback(() => {
    history.push(Navigation.Rules);
  }, [history]);

  const displayWarning = useCallback(async () => {
    if (!id) return;

    try {
      const data = await api.rules.getWarningRule(id);
      setWarningData(data);
      setVisibleWarningModal(true);
    } catch (error) {
      console.error('Failed to load warning data:', error);
      toast.error('Failed to load warning information');
    }
  }, [id]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const modifiedValue = handleChainingRuleRequest(ruleToUpdate?.chainingRules ?? [], values.chainingRules ?? []);

      const formattedValues = {
        ...values,
        ...modifiedValue,
        updateWindowStartDate: values.updateWindowStartDate
          ? moment(values.updateWindowStartDate).format('YYYY-MM-DD')
          : null,
        updateWindowEndDate: values.updateWindowEndDate
          ? moment(values.updateWindowEndDate).format('YYYY-MM-DD')
          : null,
        updateWindowStartTime: values.updateWindowStartTime
          ? moment(values.updateWindowStartTime).format('HH:mm:ss')
          : null,
        updateWindowEndTime: values.updateWindowEndTime ? moment(values.updateWindowEndTime).format('HH:mm:ss') : null,
      };

      if (ruleToUpdate) {
        await api.rules.updateRule({ ...formattedValues, id });
        toast.success('Rule was updated successfully');
      } else {
        await api.rules.createUpgradeRule(formattedValues);
        toast.success('Rule was created successfully');
      }

      form.resetFields();
      onClose();
    } catch (response) {
      if (response instanceof ApiError) {
        const errorMessage = response.error.details || Object.values(response.error.propertyErrors || {}).join(', ');
        toast.error(errorMessage);
      } else {
        toast.error('Something went wrong');
      }
      console.error('HandleSubmit error:', response);
    }
  }, [form, ruleToUpdate, id, onClose]);

  useEffect(() => {
    if (!currentDeviceTypeId) return;

    Promise.all([
      api.administration.getDeviceTypeSettings(currentDeviceTypeId),
      api.administration.getDeviceTypeVersions(currentDeviceTypeId),
    ])
      .then(([settings, versions]) => {
        setDeviceSettings(settings);
        console.log(versions);
        setDeviceVersion(versions);
      })
      .catch((error) => {
        console.error('Failed to load device type data:', error);
        toast.error('Failed to load device type information');
      });
  }, [currentDeviceTypeId]);

  useEffect(() => {
    Promise.all([api.administration.getDeviceTypes(), api.administration.getSuperGroups()])
      .then(([deviceTypes, superGroups]) => {
        setDeviceTypes(deviceTypes);
        setSuperGroups(superGroups);
      })
      .catch((error) => {
        console.error('Failed to load initial data:', error);
        toast.error('Failed to load required data');
      });
  }, []);

  useEffect(() => {
    if (!id || !deviceTypes.length || !superGroups.length) return;

    api.rules
      .getRule(id)
      .then((rule) => {
        if (!rule) return;

        const enrichedRule = {
          ...rule,
          deviceTypeName: deviceTypes.find((dt) => dt.id === rule.deviceTypeId)?.deviceName,
          superGroupName: superGroups.find((sg) => sg.id === rule.superGroupId)?.name,
          updateWindowStartDate: rule.updateWindowStartDate ? moment(rule.updateWindowStartDate) : null,
          updateWindowEndDate: rule.updateWindowEndDate ? moment(rule.updateWindowEndDate) : null,
          updateWindowStartTime: rule.updateWindowStartTime ? moment(rule.updateWindowStartTime, 'HH:mm:ss') : null,
          updateWindowEndTime: rule.updateWindowEndTime ? moment(rule.updateWindowEndTime, 'HH:mm:ss') : null,
        };

        setEnableChainingRule(!!rule.enableChainingRule);
        setEnableDateRange(!!(rule.updateWindowStartDate || rule.updateWindowEndDate));
        setEnableTimeRange(!!(rule.updateWindowStartTime || rule.updateWindowEndTime));

        setRuleToUpdate(enrichedRule as any);
      })
      .catch((error) => {
        console.error('Failed to load rule:', error);
        toast.error('Failed to load rule data');
      });
  }, [id, deviceTypes, superGroups]);

  const renderFormFields = () => (
    <>
      {/* Basic Fields */}
      <Form.Item
        className={styles.block}
        name="deviceTypeId"
        style={{ gridArea: 'deviceTypeId' }}
        label="Device Type"
        rules={[{ required: true, message: 'Please input the Device Type' }]}
      >
        <Select
          onChange={setDeviceTypeId}
          disabled={isEditMode}
          allowClear
          showSearch
          filterOption={selectSearchFilter}
        >
          {deviceTypes?.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.deviceName}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        className={styles.block}
        name="superGroupId"
        style={{ gridArea: 'superGroup' }}
        label="Scope"
        rules={[{ required: true, message: 'Please input the Scope' }]}
      >
        <Select disabled={isEditMode} allowClear showSearch filterOption={selectSearchFilter}>
          {superGroups?.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        className={styles.block}
        style={{ gridArea: 'name' }}
        name="name"
        label="Name"
        rules={[{ required: true, message: 'Name can not be empty!' }]}
      >
        <Input allowClear />
      </Form.Item>

      {/* Affected Groups (Edit mode only) */}
      {ruleToUpdate && (
        <Form.Item
          className={styles.block}
          style={{ gridArea: 'stbGroups' }}
          name="Affected StbGroups"
          label="Affected StbGroups:"
        >
          <div
            style={{
              fontWeight: '400',
              fontSize: '13px',
              backgroundColor: '#1890ff1c',
              height: 'fit-content',
              overflow: 'auto',
            }}
          >
            {ruleToUpdate?.stbGroups?.join(', ') || 'There are no affected groups'}
          </div>
        </Form.Item>
      )}

      {/* Extended Versioning Settings */}
      {settings?.enableDisablingExtendedVersioning && (
        <>
          <ExtendedVersioningCheckbox
            name={['disableExtendedVersioningSettings', 'disableExtendedLoaderVersioning']}
            label="Disable Extended Loader Version"
            checked={extendedVersioning.loader}
            onChange={() => updateExtendedVersioning('loader')}
            gridArea="extendedLoader"
          />
          <ExtendedVersioningCheckbox
            name={['disableExtendedVersioningSettings', 'disableExtendedFwVersioning']}
            label="Disable Extended FW Version"
            checked={extendedVersioning.fw}
            onChange={() => updateExtendedVersioning('fw')}
            gridArea="extendedFw"
          />
          <ExtendedVersioningCheckbox
            name={['disableExtendedVersioningSettings', 'disableExtendedUiVersioning']}
            label="Disable Extended UI Version"
            checked={extendedVersioning.ui}
            onChange={() => updateExtendedVersioning('ui')}
            gridArea="extendedUi"
          />

          {deviceSettings?.fwExtraName1 && (
            <ExtendedVersioningCheckbox
              name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw1Versioning']}
              label="Disable Extended CFW1 Version"
              checked={extendedVersioning.customFw1}
              onChange={() => updateExtendedVersioning('customFw1')}
              gridArea="extendedCustomOne"
            />
          )}

          {deviceSettings?.fwExtraName2 && (
            <ExtendedVersioningCheckbox
              name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw2Versioning']}
              label="Disable Extended CFW2 Version"
              checked={extendedVersioning.customFw2}
              onChange={() => updateExtendedVersioning('customFw2')}
              gridArea="extendedCustomTwo"
            />
          )}

          {deviceSettings?.fwExtraName3 && (
            <ExtendedVersioningCheckbox
              name={['disableExtendedVersioningSettings', 'disableExtendedCustomFw3Versioning']}
              label="Disable Extended CFW3 Version"
              checked={extendedVersioning.customFw3}
              onChange={() => updateExtendedVersioning('customFw3')}
              gridArea="extendedCustomThree"
            />
          )}
        </>
      )}

      {/* Chaining Rule */}
      {settings?.enableChainingRule && (
        <Form.Item
          name="enableChainingRule"
          className={styles.block}
          valuePropName="checked"
          style={{ gridArea: 'enableChaningRule' }}
        >
          <Checkbox onChange={() => setEnableChainingRule(!enableChainingRule)} checked={enableChainingRule}>
            Enable Chaining Rule
          </Checkbox>
        </Form.Item>
      )}

      {/* Version Fields */}
      {!enableChainingRule && (
        <>
          {deviceVersion?.LOADER && (
            <Form.Item
              className={styles.block}
              name="loaderVersionFirmwareId"
              style={{ gridArea: 'loaderVersion' }}
              label="Loader Version"
            >
              <VersionSelect versions={deviceVersion.LOADER} deviceTypeId={currentDeviceTypeId} />
            </Form.Item>
          )}
          {deviceVersion?.FW && (
            <Form.Item
              className={styles.block}
              name="fwVersionFirmwareId"
              style={{ gridArea: 'FWVersion' }}
              label="FW Version"
            >
              <VersionSelect versions={deviceVersion.FW} deviceTypeId={currentDeviceTypeId} />
            </Form.Item>
          )}
          {deviceVersion?.UI && (
            <Form.Item
              className={styles.block}
              name="uiVersionFirmwareId"
              style={{ gridArea: 'UIVersion' }}
              label="UI Version"
            >
              <VersionSelect versions={deviceVersion.UI} deviceTypeId={currentDeviceTypeId} />
            </Form.Item>
          )}
          {/* Custom Firmware Fields */}
          {deviceSettings?.fwExtraName1 && (
            <Form.Item
              className={styles.block}
              name="customFirmware1VersionId"
              style={{ gridArea: 'customOne' }}
              label={deviceSettings.fwExtraName1}
            >
              <VersionSelect versions={deviceVersion?.CUSTOFW1} deviceTypeId={currentDeviceTypeId} />
            </Form.Item>
          )}
          {deviceSettings?.fwExtraName2 && (
            <Form.Item
              className={styles.block}
              name="customFirmware2VersionId"
              style={{ gridArea: 'customTwo' }}
              label={deviceSettings.fwExtraName2}
            >
              <VersionSelect versions={deviceVersion?.CUSTOFW2} deviceTypeId={currentDeviceTypeId} />
            </Form.Item>
          )}
          {deviceSettings?.fwExtraName3 && (
            <Form.Item
              className={styles.block}
              name="customFirmware3VersionId"
              style={{ gridArea: 'customThree' }}
              label={deviceSettings.fwExtraName3}
            >
              <VersionSelect versions={deviceVersion?.CUSTOFW3} deviceTypeId={currentDeviceTypeId} />
            </Form.Item>
          )}
        </>
      )}

      {enableChainingRule && (
        <ChainingRules
          deviceVersion={deviceVersion}
          deviceTypeId={deviceTypeId}
          ruleToUpdate={ruleToUpdate}
          form={form}
        />
      )}

      {/* Version Comparison */}
      {settings?.enableCompareFwVersions && !enableChainingRule ? (
        <>
          <VersionComparisonSection
            type="fw"
            enabled={versionComparison.fw}
            onToggle={() => updateVersionComparison('fw')}
            versions={deviceVersion?.FW}
            deviceTypeId={currentDeviceTypeId}
          />
          <VersionComparisonSection
            type="ui"
            enabled={versionComparison.ui}
            onToggle={() => updateVersionComparison('ui')}
            versions={deviceVersion?.UI}
            deviceTypeId={currentDeviceTypeId}
          />
          <VersionComparisonSection
            type="loader"
            enabled={versionComparison.loader}
            onToggle={() => updateVersionComparison('loader')}
            versions={deviceVersion?.LOADER}
            deviceTypeId={currentDeviceTypeId}
          />
        </>
      ) : null}

      {/* Additional Options */}
      {deviceSettings?.allowDowngradeUr && (
        <Form.Item
          name="forceUpdate"
          valuePropName="checked"
          className={styles.block}
          style={{ gridArea: 'forcedRule' }}
        >
          <Checkbox>Forced rule</Checkbox>
        </Form.Item>
      )}

      {deviceSettings?.urSendAllAtOnce && (
        <Form.Item
          name="sendAllFirmwares"
          valuePropName="checked"
          className={styles.block}
          style={{ gridArea: 'sendFirmOnce' }}
        >
          <Checkbox>Send all firmwares at once</Checkbox>
        </Form.Item>
      )}

      {/* Update Time Window */}
      {settings?.enableTimeWindow && (
        <>
          <div className={styles.block} style={{ gridArea: 'timeWindowTitle', marginTop: '20px' }}>
            <h3>Update Time Window</h3>
            <p style={{ fontSize: '12px', color: '#666' }}>
              Configure when devices can check for updates. Enable date range, time range, or both. Overnight windows
              (e.g., 22:00-06:00) are supported.
            </p>
          </div>

          <Form.Item className={styles.block} style={{ gridArea: 'enableDateRange' }} valuePropName="checked">
            <Checkbox
              checked={enableDateRange}
              onChange={(e) => {
                setEnableDateRange(e.target.checked);
                if (!e.target.checked) {
                  form.setFieldsValue({
                    updateWindowStartDate: null,
                    updateWindowEndDate: null,
                  });
                }
              }}
            >
              Enable Date Range
            </Checkbox>
          </Form.Item>

          <Form.Item
            className={styles.block}
            name="updateWindowStartDate"
            style={{ gridArea: 'startDate' }}
            label="Start Date"
            rules={[
              { required: enableDateRange, message: 'Start date is required when date range is enabled' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const endDate = getFieldValue('updateWindowEndDate');
                  if (value && endDate && moment(value).isAfter(moment(endDate))) {
                    return Promise.reject('Start date must be before or equal to end date');
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabled={!enableDateRange} />
          </Form.Item>

          <Form.Item
            className={styles.block}
            name="updateWindowEndDate"
            style={{ gridArea: 'endDate' }}
            label="End Date"
            dependencies={['updateWindowStartDate']}
            rules={[
              { required: enableDateRange, message: 'End date is required when date range is enabled' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue('updateWindowStartDate');
                  if (value && startDate && moment(value).isBefore(moment(startDate))) {
                    return Promise.reject('End date must be after or equal to start date');
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabled={!enableDateRange} />
          </Form.Item>

          <Form.Item className={styles.block} style={{ gridArea: 'enableTimeRange' }} valuePropName="checked">
            <Checkbox
              checked={enableTimeRange}
              onChange={(e) => {
                setEnableTimeRange(e.target.checked);
                if (!e.target.checked) {
                  form.setFieldsValue({
                    updateWindowStartTime: null,
                    updateWindowEndTime: null,
                    updateWindowTimezone: 'UTC',
                  });
                }
              }}
            >
              Enable Time Range
            </Checkbox>
          </Form.Item>

          <Form.Item
            className={styles.block}
            name="updateWindowStartTime"
            style={{ gridArea: 'startTime' }}
            label="Start Time"
            rules={[{ required: enableTimeRange, message: 'Start time is required when time range is enabled' }]}
          >
            <TimePicker style={{ width: '100%' }} format="HH:mm" disabled={!enableTimeRange} />
          </Form.Item>

          <Form.Item
            className={styles.block}
            name="updateWindowEndTime"
            style={{ gridArea: 'endTime' }}
            label="End Time"
            rules={[{ required: enableTimeRange, message: 'End time is required when time range is enabled' }]}
          >
            <TimePicker style={{ width: '100%' }} format="HH:mm" disabled={!enableTimeRange} />
          </Form.Item>

          <Form.Item
            className={styles.block}
            name="updateWindowTimezone"
            style={{ gridArea: 'timezone' }}
            label="Timezone"
            initialValue="UTC"
            rules={[{ required: enableTimeRange, message: 'Timezone is required when time range is enabled' }]}
          >
            <Select showSearch placeholder="Select timezone" disabled={!enableTimeRange}>
              {TIMEZONE_OPTIONS.map((tz) => (
                <Option key={tz.value} value={tz.value}>
                  {tz.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </>
      )}
    </>
  );

  const cardTitle = isEditMode ? 'Modify Upgrade rule' : 'Add New Upgrade rule';
  const buttonText = isEditMode ? 'Update' : 'Create';
  const buttonAction = isEditMode ? displayWarning : handleSubmit;

  if (isEditMode && !ruleToUpdate) {
    return null;
  }

  return (
    <>
      <Card
        title={cardTitle}
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
              Back to Rule Page
            </Button>
            <Button type="primary" onClick={buttonAction}>
              {buttonText}
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          name="form_in_modal"
          className={styles.form}
          initialValues={isEditMode ? ruleToUpdate : initialFormValues}
        >
          {renderFormFields()}
        </Form>
      </Card>

      <WarningModal
        visible={visibleWarningModal}
        onCancel={onClose}
        onSubmit={handleSubmit}
        warningData={warningData}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        accessTab={accessTab}
        handleNext={handleNext}
      />
    </>
  );
};

export default React.memo(EditRuleModal);
